/**
 * Date Format Configuration
 * 
 * Stores the date format preference from business details.
 * Falls back to default format if not available.
 */

// Default date format (most commonly used: YYYY-MM-DD)
export const DEFAULT_DATE_FORMAT = "YYYY-MM-DD";

// Common date formats
export const DATE_FORMATS = [
  { value: "YYYY-MM-DD", label: "YYYY-MM-DD (2024-01-15)", example: "2024-01-15" },
  { value: "MM/DD/YYYY", label: "MM/DD/YYYY (01/15/2024)", example: "01/15/2024" },
  { value: "DD/MM/YYYY", label: "DD/MM/YYYY (15/01/2024)", example: "15/01/2024" },
  { value: "DD-MM-YYYY", label: "DD-MM-YYYY (15-01-2024)", example: "15-01-2024" },
  { value: "MM-DD-YYYY", label: "MM-DD-YYYY (01-15-2024)", example: "01-15-2024" },
  { value: "YYYY/MM/DD", label: "YYYY/MM/DD (2024/01/15)", example: "2024/01/15" },
  { value: "DD.MM.YYYY", label: "DD.MM.YYYY (15.01.2024)", example: "15.01.2024" },
  { value: "MMM DD, YYYY", label: "MMM DD, YYYY (Jan 15, 2024)", example: "Jan 15, 2024" },
  { value: "MMMM DD, YYYY", label: "MMMM DD, YYYY (January 15, 2024)", example: "January 15, 2024" },
];

// Store date format in memory (can be updated from business details)
let currentDateFormat = DEFAULT_DATE_FORMAT;

// Global reference to context format (updated by DateFormatContext)
let globalDateFormatRef = null;

// Function to set the global format reference (called by DateFormatContext)
export const setGlobalDateFormatRef = (ref) => {
  globalDateFormatRef = ref;
};

/**
 * Get the current date format
 * @returns {string} The current date format
 */
export const getDateFormat = () => {
  // Always check sessionStorage first (most up-to-date source of truth)
  const storedFormat = sessionStorage.getItem("business_date_format");
  if (storedFormat) {
    // Sync global ref if it exists and is different
    if (globalDateFormatRef !== null && globalDateFormatRef !== storedFormat) {
      globalDateFormatRef = storedFormat;
    }
    return storedFormat;
  }
  // If global format reference exists (from DateFormatContext), use it
  if (globalDateFormatRef !== null) {
    return globalDateFormatRef;
  }
  return currentDateFormat;
};

/**
 * Set the date format
 * @param {string} format - The date format to set
 */
export const setDateFormat = (format) => {
  if (format) {
    currentDateFormat = format;
    sessionStorage.setItem("business_date_format", format);
    // Dispatch custom event to notify DateFormatContext
    window.dispatchEvent(new CustomEvent("dateFormatUpdated"));
  }
};

/**
 * Fetch date format from business details API
 * @returns {Promise<string>} The date format from business details or default
 */
export const fetchDateFormatFromBusiness = async () => {
  try {
    const baseUrl = process.env.REACT_APP_BASE_URL;
    const vendorId = sessionStorage.getItem("vendor_id") || sessionStorage.getItem("user_id");
    
    if (!vendorId) {
      return DEFAULT_DATE_FORMAT;
    }

    // Get auth token for the request
    const token = sessionStorage.getItem("token");
    const headers = {
      "Content-Type": "application/json",
    };
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    const response = await fetch(`${baseUrl}/business_info/vendor/${vendorId}/`, {
      method: "GET",
      headers: headers,
    });

    if (response.ok) {
      const result = await response.json();
      console.log("fetchDateFormatFromBusiness API response:", result);
      
      // Handle different response structures
      let dateFormat = null;
      
      // Check if date_format is directly in result
      if (result.date_format) {
        dateFormat = result.date_format;
      }
      // Check if it's nested in result.data
      else if (result.data) {
        if (result.data.date_format) {
          dateFormat = result.data.date_format;
        }
        // If result.data is an object, check if date_format is in it
        else if (typeof result.data === 'object' && result.data.date_format) {
          dateFormat = result.data.date_format;
        }
      }
      
      if (dateFormat) {
        console.log("Found date format:", dateFormat);
        setDateFormat(dateFormat);
        return dateFormat;
      } else {
        console.warn("No date_format found in API response, using default");
      }
    } else if (response.status === 404) {
      // No business profile exists yet - use default
      console.log('No business profile found (404), using default date format');
      return DEFAULT_DATE_FORMAT;
    } else {
      console.error(`Failed to fetch date format: ${response.status} ${response.statusText}`);
    }
  } catch (error) {
    console.error("Error fetching date format from business details:", error);
  }
  
  // Return default if fetch fails or no date_format found
  return DEFAULT_DATE_FORMAT;
};

/**
 * Format a date according to the current date format
 * @param {Date|string} date - The date to format
 * @returns {string} Formatted date string
 */
/**
 * Parse a date string according to the current date format
 * @param {string} dateString - The date string to parse
 * @returns {Date|null} Parsed Date object or null if invalid
 */
export const parseDate = (dateString) => {
  if (!dateString || typeof dateString !== 'string') {
    return null;
  }

  const format = getDateFormat();
  let year, month, day;

  try {
    switch (format) {
      case "YYYY-MM-DD":
      case "YYYY/MM/DD": {
        const parts = dateString.split(format.includes('/') ? '/' : '-');
        if (parts.length === 3) {
          year = parseInt(parts[0], 10);
          month = parseInt(parts[1], 10);
          day = parseInt(parts[2], 10);
        }
        break;
      }
      case "MM/DD/YYYY":
      case "MM-DD-YYYY": {
        const parts = dateString.split(format.includes('/') ? '/' : '-');
        if (parts.length === 3) {
          month = parseInt(parts[0], 10);
          day = parseInt(parts[1], 10);
          year = parseInt(parts[2], 10);
        }
        break;
      }
      case "DD/MM/YYYY":
      case "DD-MM-YYYY":
      case "DD.MM.YYYY": {
        const parts = dateString.split(format.includes('.') ? '.' : (format.includes('/') ? '/' : '-'));
        if (parts.length === 3) {
          day = parseInt(parts[0], 10);
          month = parseInt(parts[1], 10);
          year = parseInt(parts[2], 10);
        }
        break;
      }
      case "MMM DD, YYYY":
      case "MMMM DD, YYYY": {
        // Handle "Jan 15, 2024" or "January 15, 2024"
        const match = dateString.match(/(\w+)\s+(\d+),\s+(\d+)/);
        if (match) {
          const monthNames = ["january", "february", "march", "april", "may", "june", "july", "august", "september", "october", "november", "december"];
          const monthShortNames = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
          const monthName = match[1].toLowerCase();
          month = monthNames.indexOf(monthName) + 1;
          if (month === 0) {
            month = monthShortNames.indexOf(monthName) + 1;
          }
          day = parseInt(match[2], 10);
          year = parseInt(match[3], 10);
        }
        break;
      }
      default: {
        // Try to parse as ISO format
        const date = new Date(dateString);
        if (!isNaN(date.getTime())) {
          return date;
        }
        return null;
      }
    }

    if (year && month && day) {
      const date = new Date(year, month - 1, day);
      if (!isNaN(date.getTime())) {
        return date;
      }
    }
  } catch (error) {
    console.error("Error parsing date:", error);
  }

  return null;
};

/**
 * Get placeholder text for date input based on current format
 * @returns {string} Placeholder text
 */
export const getDatePlaceholder = () => {
  const format = getDateFormat();
  const formatExample = DATE_FORMATS.find(f => f.value === format);
  return formatExample ? formatExample.example : "2024-01-15";
};

export const formatDate = (date) => {
  const dateObj = date instanceof Date ? date : new Date(date);
  if (isNaN(dateObj.getTime())) {
    return "";
  }

  const format = getDateFormat();
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, "0");
  const day = String(dateObj.getDate()).padStart(2, "0");
  const monthName = dateObj.toLocaleString("en-US", { month: "long" });
  const monthShort = dateObj.toLocaleString("en-US", { month: "short" });

  switch (format) {
    case "YYYY-MM-DD":
      return `${year}-${month}-${day}`;
    case "MM/DD/YYYY":
      return `${month}/${day}/${year}`;
    case "DD/MM/YYYY":
      return `${day}/${month}/${year}`;
    case "DD-MM-YYYY":
      return `${day}-${month}-${year}`;
    case "MM-DD-YYYY":
      return `${month}-${day}-${year}`;
    case "YYYY/MM/DD":
      return `${year}/${month}/${day}`;
    case "DD.MM.YYYY":
      return `${day}.${month}.${year}`;
    case "MMM DD, YYYY":
      return `${monthShort} ${day}, ${year}`;
    case "MMMM DD, YYYY":
      return `${monthName} ${day}, ${year}`;
    default:
      return `${year}-${month}-${day}`; // Default to YYYY-MM-DD
  }
};

export default {
  getDateFormat,
  setDateFormat,
  fetchDateFormatFromBusiness,
  formatDate,
  parseDate,
  getDatePlaceholder,
  DATE_FORMATS,
  DEFAULT_DATE_FORMAT,
};




